import type { PortalAssetMediaType } from '../../types/portalAsset.js';

// The DECLARED-type allowlist for portal asset uploads (spec §4.1, C9).
// Shared by the client (pre-validation) and the server (the upload route).
//
// ⚠️ THESE ARE CLAIMS. The browser reports a type and a size; the worker
// decides from the BYTES (detectAssetType) and fails an asset whose bytes are
// not what the name said. Nothing here is a security boundary on its own.
//
// ⚠️ Deliberately NOT server/lib/assetStorage.ts's allowlists: those serve the
// Meta ad builder (no HEIC, 10 MB / 1 GB caps) and changing them would change
// that flow.

export const PORTAL_ASSET_IMAGE_TYPES = {
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/webp': 'webp',
  'image/heic': 'heic',
  'image/heif': 'heif',
} as const;

export const PORTAL_ASSET_VIDEO_TYPES = {
  'video/mp4': 'mp4',
  'video/quicktime': 'mov',
} as const;

export const PORTAL_ASSET_MAX_IMAGE_BYTES = 50 * 1024 * 1024; // 50 MB
export const PORTAL_ASSET_MAX_VIDEO_BYTES = 2 * 1024 * 1024 * 1024; // 2 GB
export const PORTAL_ASSET_MAX_FILES_PER_REQUEST = 50;

/** Every extension an ORIGINAL may carry — the path guard reads this list. */
export const PORTAL_ASSET_ORIGINAL_EXTENSIONS: readonly string[] = [
  ...new Set([...Object.values(PORTAL_ASSET_IMAGE_TYPES), ...Object.values(PORTAL_ASSET_VIDEO_TYPES), 'jpeg']),
];

export type DeclaredAssetType = {
  contentType: string;
  mediaType: PortalAssetMediaType;
  /** The extension the ORIGINAL object is stored under. */
  ext: string;
  maxBytes: number;
};

function extensionOf(filename: string): string {
  const dot = filename.lastIndexOf('.');
  return dot === -1 ? '' : filename.slice(dot + 1).trim().toLowerCase();
}

/**
 * Resolve what a file CLAIMS to be, or null when it is not allowed.
 *
 * ⚠️ C9: some browsers (Windows, some Android) report an iPhone HEIC with an
 * EMPTY `File.type`. An empty declared type is therefore accepted when — and
 * only when — the extension is `.heic` / `.heif`. Any other empty type is
 * refused: guessing "image" from an extension for arbitrary files would let
 * the upload screen accept things the worker then has to fail.
 */
export function declaredAssetType(filename: string, declared: string): DeclaredAssetType | null {
  const type = declared.trim().toLowerCase();
  const ext = extensionOf(filename);
  if (type === '') {
    if (ext === 'heic' || ext === 'heif') {
      return { contentType: `image/${ext}`, mediaType: 'image', ext, maxBytes: PORTAL_ASSET_MAX_IMAGE_BYTES };
    }
    return null;
  }
  if (type in PORTAL_ASSET_IMAGE_TYPES) {
    return {
      contentType: type,
      mediaType: 'image',
      ext: PORTAL_ASSET_IMAGE_TYPES[type as keyof typeof PORTAL_ASSET_IMAGE_TYPES],
      maxBytes: PORTAL_ASSET_MAX_IMAGE_BYTES,
    };
  }
  if (type in PORTAL_ASSET_VIDEO_TYPES) {
    return {
      contentType: type,
      mediaType: 'video',
      ext: PORTAL_ASSET_VIDEO_TYPES[type as keyof typeof PORTAL_ASSET_VIDEO_TYPES],
      maxBytes: PORTAL_ASSET_MAX_VIDEO_BYTES,
    };
  }
  return null;
}

/** A plain-words refusal naming the file — the upload screen shows it as is. */
export function declaredTypeRefusal(filename: string): string {
  return `“${filename}” isn't a supported file. Upload JPEG, PNG, WebP or HEIC photos, or MP4 or MOV videos.`;
}

export function sizeRefusal(filename: string, maxBytes: number): string {
  const mb = Math.round(maxBytes / (1024 * 1024));
  const cap = mb >= 1024 ? `${mb / 1024} GB` : `${mb} MB`;
  return `“${filename}” is too large. The limit is ${cap}.`;
}
